import * as THREE from 'three';

/**
 * Low-level procedural mesh toolkit shared by {@link buildFloraGeometry}. Everything here is a pure
 * function of its inputs (no `Math.random`), so plant geometry stays deterministic for a given
 * `visualSeed` (spec §3.3, §17).
 *
 * The two primitives that give plants their silhouette are:
 * - {@link taperedLimb} — a generalized tube swept along a curved path with a per-node radius, used
 *   for trunks, branches, stems and grass blades.
 * - {@link blobGeometry} — a smoothly displaced sphere used for foliage clusters.
 *
 * Vertex colors are used as a baked soft-shading term (a grayscale multiplier), not as hue. The
 * seasonal foliage colour still lives on the material, so the season model keeps full control of
 * colour while the mesh contributes depth. See `FloraMaterial.ts`.
 */

/** Per-vertex shading multiplier, evaluated in the plant's local space. Typically ~[0.5, 1.15]. */
export type ShadeFn = (position: THREE.Vector3, normal: THREE.Vector3) => number;

/** One node along a limb path: a point in space and the limb's radius there. */
export interface LimbNode {
  position: THREE.Vector3;
  radius: number;
}

/**
 * Accumulates transformed source geometries into a single indexed buffer, carrying position,
 * normal, and a baked shading term in the color attribute. Source geometries are disposed as they
 * are consumed, so callers can build throwaway primitives freely.
 */
export class MeshBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly colors: number[] = [];
  private readonly indices: number[] = [];
  private vertexCount = 0;

  /** Append a geometry, optionally transformed and shaded. The source is disposed. */
  add(
    geometry: THREE.BufferGeometry | null,
    options: { matrix?: THREE.Matrix4; shade?: ShadeFn } = {},
  ): this {
    if (!geometry) return this;
    const position = geometry.getAttribute('position');
    if (!position) {
      geometry.dispose();
      return this;
    }
    if (!geometry.getAttribute('normal')) geometry.computeVertexNormals();
    const normal = geometry.getAttribute('normal');

    const { matrix, shade } = options;
    const normalMatrix = matrix ? new THREE.Matrix3().getNormalMatrix(matrix) : null;
    const p = new THREE.Vector3();
    const n = new THREE.Vector3();

    for (let i = 0; i < position.count; i += 1) {
      p.fromBufferAttribute(position, i);
      if (matrix) p.applyMatrix4(matrix);
      n.fromBufferAttribute(normal, i);
      if (normalMatrix) n.applyMatrix3(normalMatrix).normalize();

      this.positions.push(p.x, p.y, p.z);
      this.normals.push(n.x, n.y, n.z);
      const s = shade ? shade(p, n) : 1;
      this.colors.push(s, s, s);
    }

    const index = geometry.getIndex();
    if (index) {
      for (let i = 0; i < index.count; i += 1) this.indices.push(index.getX(i) + this.vertexCount);
    } else {
      for (let i = 0; i < position.count; i += 1) this.indices.push(i + this.vertexCount);
    }
    this.vertexCount += position.count;
    geometry.dispose();
    return this;
  }

  get isEmpty(): boolean {
    return this.vertexCount === 0;
  }

  /** Triangle count accumulated so far (useful for LOD budgeting and tests). */
  get triangleCount(): number {
    return this.indices.length / 3;
  }

  /** Produce the merged geometry, or `null` when nothing was added. */
  build(): THREE.BufferGeometry | null {
    if (this.vertexCount === 0) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(this.normals, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    geometry.setIndex(this.indices);
    geometry.computeBoundingSphere();
    return geometry;
  }
}

/* ------------------------------------------------------------------ */
/* Deterministic spatial noise                                         */
/* ------------------------------------------------------------------ */

function hash3(ix: number, iy: number, iz: number): number {
  let h = Math.imul(ix | 0, 374761393) ^ Math.imul(iy | 0, 668265263) ^ Math.imul(iz | 0, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Deterministic 3D value noise in [0, 1]. Purely a function of position, which matters: foliage
 * blobs are displaced by direction, so vertices that a primitive duplicates (seams, poles) receive
 * identical displacement and the surface never tears.
 */
export function valueNoise3(x: number, y: number, z: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const z0 = Math.floor(z);
  const fx = smoothstep(x - x0);
  const fy = smoothstep(y - y0);
  const fz = smoothstep(z - z0);

  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
  const corner = (dx: number, dy: number, dz: number): number => hash3(x0 + dx, y0 + dy, z0 + dz);

  const x00 = lerp(corner(0, 0, 0), corner(1, 0, 0), fx);
  const x10 = lerp(corner(0, 1, 0), corner(1, 1, 0), fx);
  const x01 = lerp(corner(0, 0, 1), corner(1, 0, 1), fx);
  const x11 = lerp(corner(0, 1, 1), corner(1, 1, 1), fx);
  return lerp(lerp(x00, x10, fy), lerp(x01, x11, fy), fz);
}

/* ------------------------------------------------------------------ */
/* Limbs — trunks, branches, stems, grass blades                       */
/* ------------------------------------------------------------------ */

function nodeAt(nodes: readonly LimbNode[], index: number): LimbNode {
  const clamped = Math.min(Math.max(index, 0), nodes.length - 1);
  const node = nodes[clamped];
  if (!node) throw new Error('taperedLimb: node list is empty');
  return node;
}

/**
 * Sweep a ring of `radialSegments` vertices along a path, tapering to each node's radius. Frames are
 * parallel-transported between nodes so the tube never twists or pinches, which is what lets a
 * trunk lean, a branch droop, and a grass blade arc over using the same primitive.
 */
export function taperedLimb(
  nodes: readonly LimbNode[],
  radialSegments = 6,
  options: { capBase?: boolean; closeTip?: boolean } = {},
): THREE.BufferGeometry | null {
  if (nodes.length < 2 || radialSegments < 3) return null;

  const positions: number[] = [];
  const indices: number[] = [];

  // Parallel-transport frame: start with any vector perpendicular to the first tangent, then rotate
  // it by the minimal rotation between consecutive tangents.
  const tangent = new THREE.Vector3();
  const prevTangent = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const binormal = new THREE.Vector3();
  const rotation = new THREE.Quaternion();

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodeAt(nodes, i);
    const prev = nodeAt(nodes, i - 1);
    const next = nodeAt(nodes, i + 1);
    tangent.subVectors(next.position, prev.position);
    if (tangent.lengthSq() < 1e-12) tangent.set(0, 1, 0);
    tangent.normalize();

    if (i === 0) {
      const reference = Math.abs(tangent.y) > 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
      normal.crossVectors(tangent, reference).normalize();
    } else {
      rotation.setFromUnitVectors(prevTangent, tangent);
      normal.applyQuaternion(rotation).normalize();
    }
    binormal.crossVectors(tangent, normal).normalize();
    prevTangent.copy(tangent);

    for (let s = 0; s < radialSegments; s += 1) {
      const angle = (s / radialSegments) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      positions.push(
        node.position.x + (normal.x * cos + binormal.x * sin) * node.radius,
        node.position.y + (normal.y * cos + binormal.y * sin) * node.radius,
        node.position.z + (normal.z * cos + binormal.z * sin) * node.radius,
      );
    }
  }

  // Side quads. Ring vertices wrap by modulo so there is no seam and normals stay continuous.
  for (let i = 0; i < nodes.length - 1; i += 1) {
    const a = i * radialSegments;
    const b = (i + 1) * radialSegments;
    for (let s = 0; s < radialSegments; s += 1) {
      const s1 = (s + 1) % radialSegments;
      indices.push(a + s, b + s, b + s1);
      indices.push(a + s, b + s1, a + s1);
    }
  }

  if (options.capBase !== false) {
    const base = nodeAt(nodes, 0).position;
    const center = positions.length / 3;
    positions.push(base.x, base.y, base.z);
    for (let s = 0; s < radialSegments; s += 1) {
      indices.push(center, (s + 1) % radialSegments, s);
    }
  }

  if (options.closeTip !== false) {
    const tip = nodeAt(nodes, nodes.length - 1).position;
    const center = positions.length / 3;
    const ring = (nodes.length - 1) * radialSegments;
    positions.push(tip.x, tip.y, tip.z);
    for (let s = 0; s < radialSegments; s += 1) {
      indices.push(center, ring + s, ring + ((s + 1) % radialSegments));
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export interface LimbPathOptions {
  base: THREE.Vector3;
  /** Initial growth direction (need not be normalized). */
  direction: THREE.Vector3;
  length: number;
  baseRadius: number;
  tipRadius: number;
  segments?: number;
  /**
   * Accumulating curvature applied along the limb — the vector the tip is pulled toward. Downward
   * for drooping branches and arcing grass, sideways for a leaning trunk.
   */
  curl?: THREE.Vector3;
  /** Extra radius multiplier at the very base, producing a root/shoulder flare. */
  flare?: number;
}

/** Build the node path for a limb: a smooth curve from `base` along `direction`, bent by `curl`. */
export function limbPath(options: LimbPathOptions): LimbNode[] {
  const segments = Math.max(2, options.segments ?? 5);
  const direction = options.direction.clone().normalize();
  const curl = options.curl ?? new THREE.Vector3();
  const flare = options.flare ?? 1;
  const nodes: LimbNode[] = [];

  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    const position = options.base
      .clone()
      .addScaledVector(direction, options.length * t)
      // Quadratic so the bend accumulates toward the tip rather than kinking at the base.
      .addScaledVector(curl, options.length * t * t);
    const taper = options.baseRadius + (options.tipRadius - options.baseRadius) * t;
    // Flare falls off quickly over the first fifth of the limb.
    const flareTerm = 1 + (flare - 1) * Math.max(0, 1 - t * 5);
    nodes.push({ position, radius: Math.max(0.0015, taper * flareTerm) });
  }
  return nodes;
}

/* ------------------------------------------------------------------ */
/* Foliage blobs                                                       */
/* ------------------------------------------------------------------ */

/**
 * A unit-radius sphere displaced by {@link valueNoise3} to read as an organic clump of leaves. The
 * original (analytic) sphere normals are kept: the displacement is gentle, so they remain a good
 * approximation and the result shades smoothly with no seam artefacts.
 */
export function blobGeometry(
  seed: number,
  options: { roughness?: number; widthSegments?: number; heightSegments?: number } = {},
): THREE.BufferGeometry {
  const roughness = options.roughness ?? 0.22;
  const geometry = new THREE.SphereGeometry(
    1,
    options.widthSegments ?? 8,
    options.heightSegments ?? 6,
  );
  const position = geometry.getAttribute('position');
  const offset = (seed % 997) * 0.613;
  const v = new THREE.Vector3();

  for (let i = 0; i < position.count; i += 1) {
    v.fromBufferAttribute(position, i);
    const coarse = valueNoise3(v.x * 1.7 + offset, v.y * 1.7 + offset, v.z * 1.7 + offset);
    const fine = valueNoise3(v.x * 4.3 - offset, v.y * 4.3 - offset, v.z * 4.3 - offset);
    const scale = 1 + (coarse - 0.5) * 2 * roughness + (fine - 0.5) * roughness * 0.5;
    position.setXYZ(i, v.x * scale, v.y * scale, v.z * scale);
  }
  position.needsUpdate = true;
  return geometry;
}

/** Convenience: a transform matrix for a blob placed at `center` with per-axis radii. */
export function blobMatrix(
  center: THREE.Vector3,
  radius: THREE.Vector3,
  rotationY = 0,
): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    center,
    new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotationY),
    radius,
  );
}

/**
 * Standard foliage shading ramp: darker toward the underside/interior of the plant, brighter toward
 * the top. `base` and `top` are world-space heights bounding the foliage mass.
 */
export function verticalShade(base: number, top: number, low = 0.62, high = 1.12): ShadeFn {
  const span = Math.max(1e-4, top - base);
  return (position) => {
    const t = Math.min(1, Math.max(0, (position.y - base) / span));
    return low + (high - low) * smoothstep(t);
  };
}
